import { ExternalLink } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useWaitForTransactionReceipt } from "wagmi";
import { useToasts } from "@/components/ui/toast-provider";

// Centralized config and helpers to keep JSX minimal and easy to tweak
type TxToastPhase = "confirm" | "pending" | "success" | "error";

const EXPLORER_BASE_URL = "https://berascan.com";
const DEFAULT_SUCCESS_IMAGE = "/poku/toast/success.png";

const BEAR_SPRITES: Record<
  TxToastPhase,
  { state: "confirming" | "pending" | "success" | "error"; src: string }
> = {
  confirm: { state: "confirming", src: "/poku/toast/info.png" },
  pending: { state: "pending", src: "/poku/toast/warning.png" },
  success: { state: "success", src: DEFAULT_SUCCESS_IMAGE },
  error: { state: "error", src: "/poku/toast/fail.png" },
};

const PRESENT_CONTINUOUS: Record<string, string> = {
  "Deposit liquidity into vault": "Depositing liquidity into vault",
  "Withdraw liquidity from vault": "Withdrawing liquidity from vault",
  "Stake LP tokens": "Staking LP tokens",
  "Unstake LP tokens": "Unstaking LP tokens",
  "Claim BGT rewards": "Claiming BGT rewards",
  "Claim oBERO rewards": "Claiming oBERO rewards",
  "Approve WBERA": "Approving WBERA",
  "Approve LP tokens": "Approving LP tokens",
  "Approve LP for staking": "Approving LP for staking",
  "Burn HENLO tokens": "Burning HENLO tokens",
};

const PAST_TENSE: Record<string, string> = {
  "Deposit liquidity into vault": "Deposited liquidity into vault",
  "Withdraw liquidity from vault": "Withdrew liquidity from vault",
  "Stake LP tokens": "Staked LP tokens",
  "Unstake LP tokens": "Unstaked LP tokens",
  "Claim BGT rewards": "Claimed BGT rewards",
  "Claim oBERO rewards": "Claimed oBERO rewards",
  "Approve WBERA": "Approved WBERA",
  "Approve LP tokens": "Approved LP tokens",
  "Approve LP for staking": "Approved LP for staking",
  "Burn HENLO tokens": "Burned HENLO tokens",
};

function asPresentContinuous(s: string) {
  return PRESENT_CONTINUOUS[s] ?? s;
}

function asPastTense(s: string) {
  return PAST_TENSE[s] ?? s;
}

function explorerTxUrl(hash: `0x${string}`) {
  return `${EXPLORER_BASE_URL}/tx/${hash}`;
}

type Count = { current: number; total: number };

function _CountBadge({ count, color }: { count?: Count; color: "blue" | "green" }) {
  if (!count || count.total <= 1) return null;
  const colorClass = color === "blue" ? "text-blue-400" : "text-green-400";
  return (
    <span className={`${colorClass} ml-2 font-pressStart2P text-[8px] font-medium`}>
      [{count.current}/{count.total}]
    </span>
  );
}

// Legacy CloseButton removed (Sonner dependency no longer used)

function _ExplorerLink({ hash }: { hash: `0x${string}` }) {
  return (
    <a
      href={explorerTxUrl(hash)}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center justify-center rounded p-1.5 text-muted-foreground transition-[color,background-color,transform] hover:bg-white/10 hover:text-white active:scale-95"
      onClick={(e) => e.stopPropagation()}
      aria-label="View on Berascan"
    >
      <ExternalLink className="h-3.5 w-3.5" />
    </a>
  );
}

interface ToastViewProps {
  phase: TxToastPhase;
  id: string | number;
  title: string;
  subtitle?: string;
  successImage?: string;
  count?: Count;
  hash?: `0x${string}`;
}

function _TransactionToastView({
  phase,
  id,
  title,
  subtitle,
  successImage,
  count,
  hash,
}: ToastViewProps) {
  const bear = BEAR_SPRITES[phase];
  const isZeroHash = typeof hash === "string" && /^0x0+$/i.test(hash);
  const _showLink = phase !== "confirm" && !!hash && !isZeroHash;
  const _bearSrc = phase === "success" && successImage ? successImage : bear.src;

  // This view is now rendered by the Radix provider; this component remains for types only.
  return null;
}

interface UseTransactionToastOptions {
  title: string;
  description?: string;
  onSuccess?: () => Promise<void> | void;
  onError?: (error: any) => void;
  successImage?: string;
  confirmations?: number;
}

export function useTransactionToast({
  title,
  onSuccess,
  onError,
  successImage = DEFAULT_SUCCESS_IMAGE,
  confirmations = 1,
}: UseTransactionToastOptions) {
  const { upsert } = useToasts();
  const hashRef = useRef<`0x${string}` | undefined>(undefined);
  const stableIdRef = useRef<string | number | undefined>(undefined);
  const transactionCountRef = useRef<Count>({ current: 1, total: 1 });
  const [hash, setHash] = useState<`0x${string}` | undefined>(undefined);
  const handledForHashRef = useRef<`0x${string}` | null>(null);

  const ensureId = () => {
    if (!stableIdRef.current) {
      stableIdRef.current = `tx-${Math.random().toString(36).slice(2)}`;
    }
    return stableIdRef.current;
  };

  const show = (
    phase: TxToastPhase,
    opts?: { subtitle?: string; hash?: `0x${string}`; titleOverride?: string },
  ) => {
    const id = String(ensureId());
    const toastTitle =
      opts?.titleOverride ||
      (phase === "confirm"
        ? "Confirm transaction"
        : phase === "pending"
          ? "Transaction pending"
          : phase === "success"
            ? "Transaction successful"
            : "Transaction failed");
    upsert({
      id,
      phase,
      title: toastTitle,
      subtitle: opts?.subtitle,
      count: transactionCountRef.current,
      hash: opts?.hash ?? hashRef.current,
      successImage,
      open: true,
    });
  };

  // Public API
  const initiate = (current: number = 1, total: number = 1) => {
    hashRef.current = undefined;
    transactionCountRef.current = { current, total };
    show("confirm", { subtitle: title });
  };

  const trackTransaction = (hash: `0x${string}`, current?: number, total?: number) => {
    hashRef.current = hash;
    setHash(hash);
    if (current !== undefined && total !== undefined)
      transactionCountRef.current = { current, total };
    show("pending", { subtitle: asPresentContinuous(title), hash });
  };

  const reject = (_message?: string) => {
    // Preserve explicit copy for user rejection
    show("error", { subtitle: title, titleOverride: "User rejected" });
  };

  const handleSuccess = () => {
    const { total } = transactionCountRef.current;
    show("success", {
      subtitle: `${asPastTense(title)}${total > 1 ? "" : ""}`,
    });
    onSuccess?.();
    transactionCountRef.current = { current: 1, total: 1 };
  };

  const handleError = (error: any) => {
    // Keep message handling minimal to avoid leaking provider-specific strings in UI
    show("error", { subtitle: title });
    onError?.(error);
  };

  // Unified exception handler: detects user-rejection vs other errors and routes accordingly
  const onException = (error: any): "rejected" | "error" => {
    try {
      const code = (error as any)?.code;
      const name = (error as any)?.name;
      const shortMsg = String((error as any)?.shortMessage || "");
      const msg = String((error as any)?.message || shortMsg || "").toLowerCase();
      const isRejected =
        code === 4001 ||
        name === "UserRejectedRequestError" ||
        shortMsg.toLowerCase().includes("rejected") ||
        msg.includes("rejected") ||
        msg.includes("denied");
      if (isRejected) {
        reject("User rejected");
        return "rejected" as const;
      }
    } catch {}
    handleError(error);
    return "error" as const;
  };

  // Receipt tracking: once a hash is provided, wait for the receipt and
  // advance the toast state automatically to success/error.
  const { isSuccess, isError, failureReason } = useWaitForTransactionReceipt({
    hash,
    confirmations,
    query: { enabled: !!hash },
  });

  useEffect(() => {
    if (!hash) return;
    // Prevent handling the same hash more than once
    if (isSuccess && handledForHashRef.current !== hash) {
      handledForHashRef.current = hash;
      handleSuccess();
    }
  }, [isSuccess, hash, handleSuccess]);

  useEffect(() => {
    if (!hash) return;
    if (isError && handledForHashRef.current !== hash) {
      handledForHashRef.current = hash;
      handleError(failureReason);
    }
  }, [isError, failureReason, hash, handleError]);

  return {
    initiate,
    trackTransaction,
    reject,
    onSuccess: handleSuccess,
    onError: handleError,
    onException,
  };
}

// Lightweight, non-hook helper used by some components/docs
// Returns imperative controls for confirm → pending → success/error flow.
export function showTransactionToast(_initialHash: `0x${string}`, _action?: string) {
  // Legacy helper – no-op stub to keep API surface stable.
  return {
    setPending: () => void 0,
    setSuccess: () => void 0,
    setError: () => void 0,
  };
}

// Headless, reusable toast API that renders a fully custom toast while
// preserving Sonner's animations/interactions. Also re-exports Sonner helpers.
export interface ToastProps {
  id: string | number;
  title: string;
  description?: string;
  button: {
    label: string;
    onClick: () => void;
  };
}

function _HeadlessToast(props: ToastProps) {
  const { title, description, button, id } = props;
  return (
    <div className="flex w-full items-center rounded-lg bg-white p-4 shadow-lg ring-1 ring-black/5 md:max-w-[364px]">
      <div className="flex flex-1 items-center">
        <div className="w-full">
          <p className="text-gray-900 text-sm font-medium">{title}</p>
          {description ? <p className="text-gray-500 mt-1 text-sm">{description}</p> : null}
        </div>
      </div>
      <div className="focus:outline-hidden ml-5 shrink-0 text-sm font-medium text-indigo-600 hover:text-indigo-500 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
        <button
          className="rounded bg-indigo-50 px-3 py-1 text-sm font-semibold text-indigo-600 hover:bg-indigo-100"
          onClick={() => {
            try {
              button.onClick();
            } finally {
              // no-op (Radix toast provider manages dismissal elsewhere)
            }
          }}
        >
          {button.label}
        </button>
      </div>
    </div>
  );
}

export function toast(_: Omit<ToastProps, "id">) {
  // Legacy no-op; headless toasts can be reintroduced with Radix if needed.
}
